import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Calendar, MapPin, ShoppingBag, Users, ArrowRight, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';

const Landing = () => {
  const { t, i18n } = useTranslation();

  const toggleLanguage = () => {
    const newLang = i18n.language === 'fr' ? 'en' : 'fr';
    i18n.changeLanguage(newLang);
    localStorage.setItem('motoconnect_language', newLang);
  };

  const features = [
    {
      icon: Calendar,
      title: t('landing.features.events.title'),
      description: t('landing.features.events.description'),
    },
    {
      icon: MapPin,
      title: t('landing.features.places.title'),
      description: t('landing.features.places.description'),
    },
    {
      icon: ShoppingBag,
      title: t('landing.features.marketplace.title'),
      description: t('landing.features.marketplace.description'),
    },
    {
      icon: Users,
      title: t('landing.features.community.title'),
      description: t('landing.features.community.description'),
    },
  ];

  return (
    <div className="min-h-screen bg-background noise">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center">
                <span className="font-heading font-bold text-xl text-primary-foreground">M</span>
              </div>
              <span className="font-heading font-bold text-xl">MOTO SOCIAL</span>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleLanguage}
                data-testid="landing-language-toggle"
                className="text-muted-foreground hover:text-foreground"
              >
                <Globe className="w-5 h-5" />
              </Button>
              <Link to="/login">
                <Button variant="ghost" data-testid="landing-login-btn">
                  {t('nav.login')}
                </Button>
              </Link>
              <Link to="/register">
                <Button className="brand-glow font-bold uppercase tracking-wider" data-testid="landing-register-btn">
                  {t('nav.register')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1633994079794-e7c76a0bf575?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          }}
        >
          <div 
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(to bottom, rgba(9,9,11,0.3) 0%, rgba(9,9,11,0.8) 70%, rgba(9,9,11,1) 100%)',
            }}
          />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 lg:px-12 pt-24 pb-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-8 lg:col-span-7">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-7xl uppercase leading-tight tracking-tight"
              >
                {t('landing.hero.title')}
              </motion.h1>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mt-6 text-lg md:text-xl text-zinc-400 max-w-2xl"
              >
                {t('landing.hero.subtitle')}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="mt-10 flex flex-col sm:flex-row gap-4"
              >
                <Link to="/register">
                  <Button 
                    size="lg" 
                    className="brand-glow font-bold uppercase tracking-wider text-lg h-14 px-8"
                    data-testid="hero-cta-btn"
                  >
                    {t('landing.hero.cta')}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href="#features">
                  <Button 
                    variant="secondary" 
                    size="lg"
                    className="font-bold uppercase tracking-wider text-lg h-14 px-8"
                    data-testid="hero-discover-btn"
                  >
                    {t('landing.hero.ctaSecondary')}
                  </Button>
                </a>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.6 }}
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
        >
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex justify-center pt-2">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 rounded-full bg-primary"
            />
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 md:py-32 relative">
        <div 
          className="absolute inset-0 opacity-30"
          style={{
            background: 'radial-gradient(circle at center, rgba(249, 115, 22, 0.15) 0%, rgba(9, 9, 11, 0) 70%)',
          }}
        />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl uppercase tracking-tight text-center"
          >
            {t('landing.features.title')}
          </motion.h2>

          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group p-6 rounded-lg bg-card border border-border hover:border-primary/50 transition-colors duration-300"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading font-bold text-xl uppercase tracking-tight">
                  {feature.title}
                </h3>
                <p className="mt-2 text-zinc-400 text-sm">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-card border border-border rounded-2xl p-8 md:p-16"
          >
            <h2 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight">
              {i18n.language === 'fr' ? 'Prêt à rouler ?' : 'Ready to ride?'}
            </h2>
            <p className="mt-4 text-zinc-400 max-w-lg mx-auto">
              {i18n.language === 'fr' 
                ? 'Rejoignez la plus grande communauté de motards en Europe.' 
                : 'Join the largest biker community in Europe.'}
            </p>
            <Link to="/register">
              <Button 
                size="lg" 
                className="mt-8 brand-glow font-bold uppercase tracking-wider text-lg h-14 px-8"
                data-testid="cta-join-btn"
              >
                {t('landing.hero.cta')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Logo & Description */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-md bg-primary flex items-center justify-center">
                  <span className="font-heading font-bold text-xl text-primary-foreground">M</span>
                </div>
                <span className="font-heading font-bold text-xl">MOTO SOCIAL</span>
              </div>
              <p className="text-sm text-zinc-400 max-w-sm">
                {i18n.language === 'fr' 
                  ? 'Le réseau social des passionnés de moto en Europe. Partagez, découvrez, roulez ensemble.'
                  : 'The social network for motorcycle enthusiasts in Europe. Share, discover, ride together.'}
              </p>
            </div>

            {/* Links */}
            <div>
              <h4 className="font-heading font-bold text-sm uppercase tracking-wider mb-4">
                {i18n.language === 'fr' ? 'Légal' : 'Legal'}
              </h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/legal" className="text-zinc-400 hover:text-primary transition-colors">
                    {i18n.language === 'fr' ? 'Mentions légales' : 'Legal Notice'}
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-zinc-400 hover:text-primary transition-colors">
                    {i18n.language === 'fr' ? 'CGU' : 'Terms of Service'}
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-zinc-400 hover:text-primary transition-colors">
                    {i18n.language === 'fr' ? 'Confidentialité & RGPD' : 'Privacy & GDPR'}
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-heading font-bold text-sm uppercase tracking-wider mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="mailto:contact@motosocial.eu" className="text-zinc-400 hover:text-primary transition-colors">
                    contact@motosocial.eu
                  </a>
                </li>
                <li>
                  <a href="mailto:privacy@motosocial.eu" className="text-zinc-400 hover:text-primary transition-colors">
                    privacy@motosocial.eu
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom */}
          <div className="pt-8 border-t border-border flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-zinc-500">
              © {new Date().getFullYear()} Moto Social. {i18n.language === 'fr' ? 'Tous droits réservés.' : 'All rights reserved.'}
            </p>
            <div className="flex items-center gap-4 text-sm text-zinc-500">
              <span>🇫🇷 🇪🇺</span>
              <span>{i18n.language === 'fr' ? 'Hébergé en Europe' : 'Hosted in Europe'}</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
